/**
 * Agent 路由服务
 * 负责根据用户输入判断应该使用哪个 agent
 */

import { OptionType } from '@/proto/field';
import { llmOauthService } from '@/service/llm-oauth';

export interface AgentRouteRequest {
  userInput: string;
  availableAgents: string[];
  context?: {
    userId?: string;
    productId?: string;
    sessionId?: string;
  };
}

export interface AgentRouteResponse {
  targetAgent: string;
  confidence?: number;
  reason?: string;
}

/**
 * 调用 dispatch 接口进行 agent 路由
 */
export const routeToAgent = async (
  userInput: string,
  availableAgents: OptionType[],
  _context?: AgentRouteRequest['context'],
): Promise<string> => {
  // 如果没有可用 agents，返回默认值
  if (!availableAgents || availableAgents.length === 0) {
    return '';
  }

  // 如果只有一个 agent，直接返回
  if (availableAgents.length === 1) {
    return (availableAgents[0]?.value ?? '') as string;
  }

  // 使用 dispatch 接口
  return await routeToAgentByDispatch(userInput, availableAgents);
};

/**
 * 通过 dispatch 接口获取 agent
 */
const routeToAgentByDispatch = async (
  userInput: string,
  availableAgents: OptionType[],
): Promise<string> => {
  try {
    // 构造输入
    const requestInput = `
      用户输入： ${userInput},
      可用 agents： ${JSON.stringify(availableAgents)}
    `;

    const agentValue = await llmOauthService.dispatchRouter(requestInput);

    if (agentValue) {
      // 根据 agentValue 查找对应的 agent
      const targetAgent = availableAgents.find(agent => agent.value === agentValue);
      if (targetAgent) {
        return targetAgent.value as string;
      }
    }

    // 如果没有找到对应的 agent，返回第一个
    return availableAgents[0]?.value as string;
  } catch (error) {
    // 错误降级：返回第一个可用 agent
    return availableAgents[0]?.value as string;
  }
};

/**
 * 本地关键词匹配（作为备选方案或快速路由）
 */
export const quickRouteByKeywords = (
  userInput: string,
  agentOptions: Array<{
    value: string;
    keywords?: string[];
  }>,
): string | null => {
  const input = userInput.toLowerCase();

  for (const agent of agentOptions) {
    if (agent.keywords) {
      for (const keyword of agent.keywords) {
        if (input.includes(keyword.toLowerCase())) {
          return agent.value;
        }
      }
    }
  }

  return null;
};

/**
 * Agent 路由配置
 * 可以根据实际情况扩展
 */
export const AGENT_ROUTE_CONFIG = {
  // 路由超时时间（毫秒）
  timeout: 50000,

  // 是否启用本地关键词匹配作为降级
  enableKeywordFallback: true,

  // 是否在路由时显示 loading 状态
  showLoadingState: true,

  // 默认 agent
  defaultAgent: 'coordinator',
};
